import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, FileText } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  isProdutoLeadAdequacoesReformasFv,
  isProdutoLeadIncendio,
  PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV,
} from '@/constants/leadsProduto';
import { montarEscopoResumoProposta } from '@/utils/contratosAdequacoesUtils';
import {
  completarEnderecoViaCep,
  enderecoIncompleto,
  enriquecerCamposComIrmaos,
  camposClienteFromLead,
  preferirPreenchido,
} from '@/utils/contratoClienteFromLead';

interface ContratoAdequacaoFormularioProps {
  proposta: any;
  onSuccess: (pdfUrl: string) => void;
  onCancel: () => void;
}

const formatarCPF = (cpf: string) => {
  const cpfLimpo = cpf.replace(/\D/g, '');
  return cpfLimpo.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatarCEP = (cep: string) => {
  const cepLimpo = cep.replace(/\D/g, '');
  return cepLimpo.replace(/(\d{5})(\d{3})/, '$1-$2');
};

const formatarCNPJ = (cnpj: string) => {
  const cnpjLimpo = cnpj.replace(/\D/g, '');
  return cnpjLimpo.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

export default function ContratoAdequacaoFormulario({ proposta, onSuccess, onCancel }: ContratoAdequacaoFormularioProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const leadInicial = camposClienteFromLead(proposta.leads);
  const [tipoCliente, setTipoCliente] = useState<'PF' | 'PJ'>(leadInicial?.tipoCliente || 'PF');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const escopoInicial = montarEscopoResumoProposta(proposta);
  const [dados, setDados] = useState({
    razao_social_empresa: 'RST SERVIÇOS DE ENGENHARIA LTDA',
    nome_fantasia_empresa: 'SUN7 ENERGY',
    cnpj_empresa: '37395997000136',
    endereco_empresa: 'R. Belmira Isabel Martins',
    numero_empresa: '42',
    bairro_empresa: 'Estreito',
    cidade_empresa: 'Florianópolis',
    estado_empresa: 'SC',
    cep_empresa: '88075145',
    representante_legal_empresa: 'RAFAEL SILVA TEIXEIRA',
    cpf_representante_empresa: '08840379916',
    cargo_representante_empresa: 'Diretor Eng.',
    
    nome_cliente: leadInicial?.nome_cliente || '',
    nacionalidade: 'brasileira',
    estado_civil: '',
    profissao: '',
    cpf: leadInicial?.cpf || '',
    cnpj: leadInicial?.cnpj || '',
    representante_legal: leadInicial?.representante_legal || '',
    cpf_representante: leadInicial?.cpf_representante || '',
    endereco: leadInicial?.endereco || '',
    numero: leadInicial?.numero || '',
    bairro: leadInicial?.bairro || '',
    cidade: leadInicial?.cidade || '',
    estado: leadInicial?.estado || '',
    cep: leadInicial?.cep || '',
    forma_pagamento: 'pix',
    forma_pagamento_assinatura: 'pix',
    forma_pagamento_intermediaria: 'pix',
    forma_pagamento_conclusao: 'pix',
    modalidade_pagamento: 'integral_assinatura',
    percentual_assinatura: 50,
    percentual_intermediaria: 33,
    percentual_conclusao: 50,
    numero_parcelas: 1,
    valor_parcela: proposta.valor_total || 0,
    numero_parcelas_assinatura: 1,
    valor_parcela_assinatura: 0,
    numero_parcelas_intermediaria: 1,
    valor_parcela_intermediaria: 0,
    numero_parcelas_conclusao: 1,
    valor_parcela_conclusao: 0,
    valor_total: proposta.valor_total || 0,
    escopo_resumo: escopoInicial,
    cidade_assinatura: 'Florianópolis',
  });

  useEffect(() => {
    const carregarDadosSalvos = async () => {
      const leadId = proposta.lead_id || proposta.leads?.id;
      const { data: lead, error: leadError } = leadId
        ? await supabase.from('leads').select('*').eq('id', leadId).single()
        : { data: null, error: null };

      if (leadError) {
        console.error('Erro ao carregar lead para contrato:', leadError);
      }

      const fromLead =
        (await enriquecerCamposComIrmaos(lead, supabase as any)) ||
        camposClienteFromLead(lead);
      if (fromLead) {
        setTipoCliente(fromLead.tipoCliente);
      }

      const { data: contratosSalvos } = await supabase
        .from('contratos_adequacoes_reformas')
        .select('*')
        .eq('proposta_id', proposta.id)
        .order('created_at', { ascending: false });

      const contratoSalvo =
        [...(contratosSalvos || [])].sort((a, b) => {
          const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
          const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
          return tb - ta;
        })[0] ?? null;

      if (contratoSalvo?.cnpj && String(contratoSalvo.cnpj).trim()) {
        setTipoCliente('PJ');
      } else if (contratoSalvo?.cpf && String(contratoSalvo.cpf).trim() && !fromLead) {
        setTipoCliente('PF');
      } else if (fromLead) {
        setTipoCliente(fromLead.tipoCliente);
      }

      let mergedCliente = {
        nome_cliente: preferirPreenchido(contratoSalvo?.nome_cliente, fromLead?.nome_cliente),
        nacionalidade: preferirPreenchido(contratoSalvo?.nacionalidade, 'brasileira'),
        estado_civil: preferirPreenchido(contratoSalvo?.estado_civil, ''),
        profissao: preferirPreenchido(contratoSalvo?.profissao, ''),
        cpf: preferirPreenchido(contratoSalvo?.cpf, fromLead?.cpf),
        cnpj: preferirPreenchido(contratoSalvo?.cnpj, fromLead?.cnpj),
        representante_legal: preferirPreenchido(
          contratoSalvo?.representante_legal,
          fromLead?.representante_legal
        ).toUpperCase(),
        cpf_representante: preferirPreenchido(
          contratoSalvo?.cpf_representante,
          fromLead?.cpf_representante
        ),
        endereco: preferirPreenchido(contratoSalvo?.endereco, fromLead?.endereco),
        numero: preferirPreenchido(contratoSalvo?.numero, fromLead?.numero),
        bairro: preferirPreenchido(contratoSalvo?.bairro, fromLead?.bairro),
        cidade: preferirPreenchido(contratoSalvo?.cidade, fromLead?.cidade),
        estado: preferirPreenchido(contratoSalvo?.estado, fromLead?.estado),
        cep: preferirPreenchido(contratoSalvo?.cep, fromLead?.cep).replace(/\D/g, ''),
      };

      if (mergedCliente.cep.length === 8 && enderecoIncompleto(mergedCliente)) {
        const viaCep = await completarEnderecoViaCep(mergedCliente.cep);
        if (viaCep) {
          mergedCliente = {
            ...mergedCliente,
            endereco: preferirPreenchido(mergedCliente.endereco, viaCep.endereco),
            bairro: preferirPreenchido(mergedCliente.bairro, viaCep.bairro),
            cidade: preferirPreenchido(mergedCliente.cidade, viaCep.cidade),
            estado: preferirPreenchido(mergedCliente.estado, viaCep.estado),
          };
        }
      }

      setDados((prev) => ({
        ...prev,
        ...mergedCliente,
        ...(contratoSalvo
          ? {
              razao_social_empresa:
                preferirPreenchido(contratoSalvo.razao_social_empresa, prev.razao_social_empresa) ||
                prev.razao_social_empresa,
              nome_fantasia_empresa:
                preferirPreenchido(contratoSalvo.nome_fantasia_empresa, prev.nome_fantasia_empresa) ||
                prev.nome_fantasia_empresa,
              cnpj_empresa:
                preferirPreenchido(contratoSalvo.cnpj_empresa, prev.cnpj_empresa) || prev.cnpj_empresa,
              endereco_empresa:
                preferirPreenchido(contratoSalvo.endereco_empresa, prev.endereco_empresa) ||
                prev.endereco_empresa,
              numero_empresa:
                preferirPreenchido(contratoSalvo.numero_empresa, prev.numero_empresa) ||
                prev.numero_empresa,
              bairro_empresa:
                preferirPreenchido(contratoSalvo.bairro_empresa, prev.bairro_empresa) ||
                prev.bairro_empresa,
              cidade_empresa:
                preferirPreenchido(contratoSalvo.cidade_empresa, prev.cidade_empresa) ||
                prev.cidade_empresa,
              estado_empresa:
                preferirPreenchido(contratoSalvo.estado_empresa, prev.estado_empresa) ||
                prev.estado_empresa,
              cep_empresa:
                preferirPreenchido(contratoSalvo.cep_empresa, prev.cep_empresa) || prev.cep_empresa,
              representante_legal_empresa:
                preferirPreenchido(
                  contratoSalvo.representante_legal_empresa,
                  prev.representante_legal_empresa
                ) || prev.representante_legal_empresa,
              cpf_representante_empresa:
                preferirPreenchido(
                  contratoSalvo.cpf_representante_empresa,
                  prev.cpf_representante_empresa
                ) || prev.cpf_representante_empresa,
              cargo_representante_empresa:
                preferirPreenchido(
                  contratoSalvo.cargo_representante_empresa,
                  prev.cargo_representante_empresa
                ) || prev.cargo_representante_empresa,
              forma_pagamento: contratoSalvo.forma_pagamento || prev.forma_pagamento,
              forma_pagamento_assinatura:
                contratoSalvo.forma_pagamento_assinatura || prev.forma_pagamento_assinatura,
              forma_pagamento_intermediaria:
                contratoSalvo.forma_pagamento_intermediaria || prev.forma_pagamento_intermediaria,
              forma_pagamento_conclusao:
                contratoSalvo.forma_pagamento_conclusao || prev.forma_pagamento_conclusao,
              modalidade_pagamento: contratoSalvo.modalidade_pagamento || prev.modalidade_pagamento,
              percentual_assinatura: Number(
                contratoSalvo.percentual_assinatura ?? prev.percentual_assinatura
              ),
              percentual_intermediaria: Number(
                contratoSalvo.percentual_intermediaria ?? prev.percentual_intermediaria
              ),
              percentual_conclusao: Number(
                contratoSalvo.percentual_conclusao ?? prev.percentual_conclusao
              ),
              numero_parcelas: Number(contratoSalvo.numero_parcelas ?? prev.numero_parcelas),
              valor_parcela: Number(contratoSalvo.valor_parcela ?? prev.valor_parcela),
              numero_parcelas_assinatura: Number(
                contratoSalvo.numero_parcelas_assinatura ?? prev.numero_parcelas_assinatura
              ),
              valor_parcela_assinatura: Number(
                contratoSalvo.valor_parcela_assinatura ?? prev.valor_parcela_assinatura
              ),
              numero_parcelas_intermediaria: Number(
                contratoSalvo.numero_parcelas_intermediaria ?? prev.numero_parcelas_intermediaria
              ),
              valor_parcela_intermediaria: Number(
                contratoSalvo.valor_parcela_intermediaria ?? prev.valor_parcela_intermediaria
              ),
              numero_parcelas_conclusao: Number(
                contratoSalvo.numero_parcelas_conclusao ?? prev.numero_parcelas_conclusao
              ),
              valor_parcela_conclusao: Number(
                contratoSalvo.valor_parcela_conclusao ?? prev.valor_parcela_conclusao
              ),
              valor_total: Number(contratoSalvo.valor_total ?? prev.valor_total),
              escopo_resumo: contratoSalvo.escopo_resumo || prev.escopo_resumo,
              cidade_assinatura: contratoSalvo.cidade_assinatura || prev.cidade_assinatura,
            }
          : {}),
      }));
    };

    carregarDadosSalvos();
  }, [proposta.lead_id, proposta.leads?.id, proposta.id]);

  const fetchAddressFromCEP = async (cepValue: string) => {
    const viaCep = await completarEnderecoViaCep(cepValue);
    if (!viaCep) return;
    setDados((prev) => ({
      ...prev,
      endereco: viaCep.endereco || prev.endereco,
      cidade: viaCep.cidade || prev.cidade,
      estado: viaCep.estado || prev.estado,
      bairro: viaCep.bairro || prev.bairro,
    }));
  };

  const validarCampos = (): boolean => {
    const novosErros: Record<string, string> = {};

    // Validação campos da empresa
    if (!dados.razao_social_empresa?.trim()) novosErros.razao_social_empresa = 'Razão social da empresa é obrigatória';
    if (!dados.cnpj_empresa?.trim()) novosErros.cnpj_empresa = 'CNPJ da empresa é obrigatório';
    if (!dados.representante_legal_empresa?.trim()) novosErros.representante_legal_empresa = 'Representante legal da empresa é obrigatório';
    if (!dados.cpf_representante_empresa?.trim()) novosErros.cpf_representante_empresa = 'CPF do representante é obrigatório';

    // Validação campos do cliente
    if (tipoCliente === 'PF') {
      if (!dados.nome_cliente?.trim()) novosErros.nome_cliente = 'Nome completo é obrigatório';
      if (!dados.nacionalidade?.trim()) novosErros.nacionalidade = 'Nacionalidade é obrigatória';
      if (!dados.estado_civil?.trim()) novosErros.estado_civil = 'Estado civil é obrigatório';
      if (!dados.profissao?.trim()) novosErros.profissao = 'Profissão é obrigatória';
      if (!dados.cpf?.trim()) novosErros.cpf = 'CPF é obrigatório';
    } else {
      if (!dados.nome_cliente?.trim()) novosErros.nome_cliente = 'Razão social é obrigatória';
      if (!dados.cnpj?.trim()) novosErros.cnpj = 'CNPJ é obrigatório';
      if (!dados.representante_legal?.trim()) novosErros.representante_legal = 'Representante legal é obrigatório';
      if (!dados.cpf_representante?.trim()) novosErros.cpf_representante = 'CPF do representante é obrigatório';
    }

    // Validação endereço (obrigatório para todos)
    if (!dados.endereco?.trim()) novosErros.endereco = 'Endereço é obrigatório';
    if (!dados.numero?.trim()) novosErros.numero = 'Número é obrigatório';
    if (!dados.bairro?.trim()) novosErros.bairro = 'Bairro é obrigatório';
    if (!dados.cidade?.trim()) novosErros.cidade = 'Cidade é obrigatória';
    if (!dados.estado?.trim()) novosErros.estado = 'Estado é obrigatório';
    if (!dados.cep?.trim()) novosErros.cep = 'CEP é obrigatório';

    // Validação condições de pagamento
    if (!dados.forma_pagamento?.trim()) novosErros.forma_pagamento = 'Forma de pagamento é obrigatória';
    if (!dados.valor_total || dados.valor_total <= 0) novosErros.valor_total = 'Valor total deve ser maior que zero';
    if (!dados.escopo_resumo?.trim()) novosErros.escopo_resumo = 'Escopo dos serviços é obrigatório';
    
    if (dados.modalidade_pagamento === 'assinatura_conclusao') {
      const soma = Number(dados.percentual_assinatura) + Number(dados.percentual_conclusao);
      if (soma !== 100) {
        novosErros.percentuais = 'A soma do percentual de assinatura e conclusão deve ser exatamente 100%';
      }
    }

    if (dados.modalidade_pagamento === 'tres_parcelas') {
      const soma =
        Number(dados.percentual_assinatura) +
        Number(dados.percentual_intermediaria) +
        Number(dados.percentual_conclusao);
      if (soma !== 100) {
        novosErros.percentuais = 'A soma dos percentuais das 3 parcelas deve ser exatamente 100%';
      }
    }

    if (!dados.cidade_assinatura?.trim()) novosErros.cidade_assinatura = 'Cidade de assinatura é obrigatória';

    setErrors(novosErros);

    if (Object.keys(novosErros).length > 0) {
      toast({
        title: 'Campos obrigatórios não preenchidos',
        description: 'Por favor, preencha todos os campos obrigatórios antes de gerar o contrato.',
        variant: 'destructive'
      });
      return false;
    }

    return true;
  };

  const handleGerarContrato = async () => {
    if (!validarCampos()) {
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // 1. Reutilizar contrato existente da proposta (evita rascunhos duplicados na aba Contratos)
      const { data: contratosExistentes, error: buscaError } = await supabase
        .from('contratos_adequacoes_reformas')
        .select('id, contrato_pdf_url, created_at')
        .eq('proposta_id', proposta.id)
        .order('created_at', { ascending: false });

      if (buscaError) throw buscaError;

      const ordenados = [...(contratosExistentes || [])].sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
      const contratoExistente =
        ordenados.find((c) => Boolean(c.contrato_pdf_url)) ?? ordenados[0];

      const payloadContrato = {
        proposta_id: proposta.id,
        lead_id: proposta.lead_id,
        created_by: user?.id,
        status: 'rascunho',
        ...dados,
      };

      let contrato;
      let contratoError;

      if (contratoExistente?.id) {
        ({ data: contrato, error: contratoError } = await supabase
          .from('contratos_adequacoes_reformas')
          .update(payloadContrato)
          .eq('id', contratoExistente.id)
          .select()
          .single());
      } else {
        ({ data: contrato, error: contratoError } = await supabase
          .from('contratos_adequacoes_reformas')
          .insert(payloadContrato)
          .select()
          .single());
      }

      if (contratoError) throw contratoError;

      // 2. Chamar a API para gerar o PDF
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch('/api/contratos-adequacoes-reformas/gerar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ contratoId: contrato.id })
      });

      if (!response.ok) throw new Error('Falha ao gerar PDF do contrato');
      
      const result = await response.json();
      onSuccess(result.pdfUrl);
    } catch (error) {
      console.error(error);
      toast({
        title: 'Erro ao gerar contrato',
        description: error instanceof Error ? error.message : 'Ocorreu um erro inesperado',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const isLinhaFv = isProdutoLeadAdequacoesReformasFv(proposta?.leads?.produto);
  const isLinhaIncendio = isProdutoLeadIncendio(proposta?.leads?.produto);
  const modalidadeComEtapas =
    dados.modalidade_pagamento === 'assinatura_conclusao' || dados.modalidade_pagamento === 'tres_parcelas';

  const aplicarModalidadePagamento = (value: string) => {
    if (value === 'tres_parcelas') {
      setDados({
        ...dados,
        modalidade_pagamento: value,
        percentual_assinatura: 33,
        percentual_intermediaria: 33,
        percentual_conclusao: 34,
        valor_parcela_assinatura: Number(((dados.valor_total * 33) / 100 / dados.numero_parcelas_assinatura).toFixed(2)),
        valor_parcela_intermediaria: Number(((dados.valor_total * 33) / 100 / dados.numero_parcelas_intermediaria).toFixed(2)),
        valor_parcela_conclusao: Number(((dados.valor_total * 34) / 100 / dados.numero_parcelas_conclusao).toFixed(2)),
      });
    } else if (value === 'assinatura_conclusao') {
      setDados({
        ...dados,
        modalidade_pagamento: value,
        percentual_assinatura: 50,
        percentual_conclusao: 50,
        valor_parcela_assinatura: Number(((dados.valor_total * 50) / 100 / dados.numero_parcelas_assinatura).toFixed(2)),
        valor_parcela_conclusao: Number(((dados.valor_total * 50) / 100 / dados.numero_parcelas_conclusao).toFixed(2)),
      });
    } else {
      setDados({ ...dados, modalidade_pagamento: value });
    }
    clearError('percentuais');
  };

  const opcoesFormaPagamento = (
    <>
      <SelectItem value="pix">Pix</SelectItem>
      <SelectItem value="boleto">Boleto</SelectItem>
      <SelectItem value="cartão de crédito">Cartão de Crédito</SelectItem>
      <SelectItem value="cartão de débito">Cartão de Débito</SelectItem>
      <SelectItem value="transferencia bancária">Transferência Bancária</SelectItem>
      <SelectItem value="dinheiro">Dinheiro</SelectItem>
    </>
  );

  return (
    <div className="space-y-6 max-h-[80vh] overflow-y-auto p-1">
      <Alert>
        <AlertDescription>
          {isLinhaFv ? (
            <>
              Será gerado o contrato de <strong>{PRODUTO_LEAD_ADEQUACOES_REFORMAS_FV}</strong>,
              com o escopo da proposta aceita. Revise valores e condições de pagamento antes de gerar o PDF.
            </>
          ) : isLinhaIncendio ? (
            <>
              Será gerado o contrato de <strong>sistema de alarme ou detecção automática de incêndio</strong>,
              com o escopo da proposta aceita. Revise valores e condições de pagamento antes de gerar o PDF.
            </>
          ) : (
            <>
              Será gerado o contrato de <strong>projeto elétrico e aprovação junto à CELESC</strong>
              {' '}(engenharia consultiva — sem execução de obra). Revise valores e condições de pagamento antes de gerar o PDF.
            </>
          )}
        </AlertDescription>
      </Alert>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Informações da Empresa Contratada</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Razão Social <span className="text-red-500">*</span></Label>
            <Input 
              value={dados.razao_social_empresa} 
              onChange={e => {
                setDados({...dados, razao_social_empresa: e.target.value});
                clearError('razao_social_empresa');
              }}
              className={errors.razao_social_empresa ? 'border-red-500' : ''}
            />
            {errors.razao_social_empresa && <p className="text-sm text-red-500">{errors.razao_social_empresa}</p>}
          </div>
          <div className="space-y-2">
            <Label>Nome Fantasia</Label>
            <Input value={dados.nome_fantasia_empresa} onChange={e => setDados({...dados, nome_fantasia_empresa: e.target.value})} />
          </div>
          <div className="space-y-2">
            <Label>CNPJ <span className="text-red-500">*</span></Label>
            <Input 
              value={formatarCNPJ(dados.cnpj_empresa)} 
              onChange={e => {
                setDados({...dados, cnpj_empresa: e.target.value.replace(/\D/g, '')});
                clearError('cnpj_empresa');
              }}
              className={errors.cnpj_empresa ? 'border-red-500' : ''}
            />
            {errors.cnpj_empresa && <p className="text-sm text-red-500">{errors.cnpj_empresa}</p>}
          </div>
          <div className="space-y-2">
            <Label>Representante Legal <span className="text-red-500">*</span></Label>
            <Input 
              value={dados.representante_legal_empresa} 
              onChange={e => {
                setDados({...dados, representante_legal_empresa: e.target.value.toUpperCase()});
                clearError('representante_legal_empresa');
              }}
              className={errors.representante_legal_empresa ? 'border-red-500' : ''}
            />
            {errors.representante_legal_empresa && <p className="text-sm text-red-500">{errors.representante_legal_empresa}</p>}
          </div>
          <div className="space-y-2">
            <Label>CPF do Representante <span className="text-red-500">*</span></Label>
            <Input 
              value={formatarCPF(dados.cpf_representante_empresa)} 
              onChange={e => {
                setDados({...dados, cpf_representante_empresa: e.target.value.replace(/\D/g, '')});
                clearError('cpf_representante_empresa');
              }}
              className={errors.cpf_representante_empresa ? 'border-red-500' : ''}
            />
            {errors.cpf_representante_empresa && <p className="text-sm text-red-500">{errors.cpf_representante_empresa}</p>}
          </div>
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Informações do Cliente (Contratante)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tipoCliente === 'PF' ? (
            <>
              <div className="space-y-2">
                <Label>Nome Completo <span className="text-red-500">*</span></Label>
                <Input 
                  value={dados.nome_cliente} 
                  onChange={e => {
                    setDados({...dados, nome_cliente: e.target.value});
                    clearError('nome_cliente');
                  }}
                  className={errors.nome_cliente ? 'border-red-500' : ''}
                />
                {errors.nome_cliente && <p className="text-sm text-red-500">{errors.nome_cliente}</p>}
              </div>
              <div className="space-y-2">
                <Label>Nacionalidade <span className="text-red-500">*</span></Label>
                <Input 
                  value={dados.nacionalidade} 
                  onChange={e => {
                    setDados({...dados, nacionalidade: e.target.value});
                    clearError('nacionalidade');
                  }}
                  className={errors.nacionalidade ? 'border-red-500' : ''}
                />
                {errors.nacionalidade && <p className="text-sm text-red-500">{errors.nacionalidade}</p>}
              </div>
              <div className="space-y-2">
                <Label>Estado Civil <span className="text-red-500">*</span></Label>
                <Input 
                  value={dados.estado_civil} 
                  onChange={e => {
                    setDados({...dados, estado_civil: e.target.value});
                    clearError('estado_civil');
                  }}
                  className={errors.estado_civil ? 'border-red-500' : ''}
                />
                {errors.estado_civil && <p className="text-sm text-red-500">{errors.estado_civil}</p>}
              </div>
              <div className="space-y-2">
                <Label>Profissão <span className="text-red-500">*</span></Label>
                <Input 
                  value={dados.profissao} 
                  onChange={e => {
                    setDados({...dados, profissao: e.target.value});
                    clearError('profissao');
                  }}
                  className={errors.profissao ? 'border-red-500' : ''}
                />
                {errors.profissao && <p className="text-sm text-red-500">{errors.profissao}</p>}
              </div>
              <div className="space-y-2">
                <Label>CPF <span className="text-red-500">*</span></Label>
                <Input 
                  value={formatarCPF(dados.cpf)} 
                  onChange={e => {
                    setDados({...dados, cpf: e.target.value.replace(/\D/g, '')});
                    clearError('cpf');
                  }}
                  className={errors.cpf ? 'border-red-500' : ''}
                />
                {errors.cpf && <p className="text-sm text-red-500">{errors.cpf}</p>}
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Razão Social <span className="text-red-500">*</span></Label>
                <Input 
                  value={dados.nome_cliente} 
                  onChange={e => {
                    setDados({...dados, nome_cliente: e.target.value});
                    clearError('nome_cliente');
                  }}
                  className={errors.nome_cliente ? 'border-red-500' : ''}
                />
                {errors.nome_cliente && <p className="text-sm text-red-500">{errors.nome_cliente}</p>}
              </div>
              <div className="space-y-2">
                <Label>CNPJ <span className="text-red-500">*</span></Label>
                <Input 
                  value={formatarCNPJ(dados.cnpj)} 
                  onChange={e => {
                    setDados({...dados, cnpj: e.target.value.replace(/\D/g, '')});
                    clearError('cnpj');
                  }}
                  className={errors.cnpj ? 'border-red-500' : ''}
                />
                {errors.cnpj && <p className="text-sm text-red-500">{errors.cnpj}</p>}
              </div>
              <div className="space-y-2">
                <Label>Representante Legal <span className="text-red-500">*</span></Label>
                <Input 
                  value={dados.representante_legal} 
                  onChange={e => {
                    setDados({...dados, representante_legal: e.target.value.toUpperCase()});
                    clearError('representante_legal');
                  }}
                  className={errors.representante_legal ? 'border-red-500' : ''}
                />
                {errors.representante_legal && <p className="text-sm text-red-500">{errors.representante_legal}</p>}
              </div>
              <div className="space-y-2">
                <Label>CPF do Representante <span className="text-red-500">*</span></Label>
                <Input 
                  value={formatarCPF(dados.cpf_representante)} 
                  onChange={e => {
                    setDados({...dados, cpf_representante: e.target.value.replace(/\D/g, '')});
                    clearError('cpf_representante');
                  }}
                  className={errors.cpf_representante ? 'border-red-500' : ''}
                />
                {errors.cpf_representante && <p className="text-sm text-red-500">{errors.cpf_representante}</p>}
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>CEP <span className="text-red-500">*</span></Label>
            <Input 
              value={formatarCEP(dados.cep)} 
              onChange={e => {
                const cepLimpo = e.target.value.replace(/\D/g, '');
                setDados({...dados, cep: cepLimpo});
                clearError('cep');
                // Buscar endereço quando CEP tiver 8 dígitos
                if (cepLimpo.length === 8) {
                  fetchAddressFromCEP(cepLimpo);
                }
              }}
              maxLength={9}
              placeholder="00000-000"
              className={errors.cep ? 'border-red-500' : ''}
            />
            {errors.cep && <p className="text-sm text-red-500">{errors.cep}</p>}
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label>Endereço <span className="text-red-500">*</span></Label>
            <Input 
              value={dados.endereco} 
              onChange={e => {
                setDados({...dados, endereco: e.target.value});
                clearError('endereco');
              }}
              className={errors.endereco ? 'border-red-500' : ''}
            />
            {errors.endereco && <p className="text-sm text-red-500">{errors.endereco}</p>}
          </div>
          <div className="space-y-2">
            <Label>Número <span className="text-red-500">*</span></Label>
            <Input 
              value={dados.numero} 
              onChange={e => {
                setDados({...dados, numero: e.target.value});
                clearError('numero');
              }}
              className={errors.numero ? 'border-red-500' : ''}
            />
            {errors.numero && <p className="text-sm text-red-500">{errors.numero}</p>}
          </div>
          <div className="space-y-2">
            <Label>Bairro <span className="text-red-500">*</span></Label>
            <Input 
              value={dados.bairro} 
              onChange={e => {
                setDados({...dados, bairro: e.target.value});
                clearError('bairro');
              }}
              className={errors.bairro ? 'border-red-500' : ''}
            />
            {errors.bairro && <p className="text-sm text-red-500">{errors.bairro}</p>}
          </div>
          <div className="space-y-2">
            <Label>Cidade <span className="text-red-500">*</span></Label>
            <Input 
              value={dados.cidade} 
              onChange={e => {
                setDados({...dados, cidade: e.target.value});
                clearError('cidade');
              }}
              className={errors.cidade ? 'border-red-500' : ''}
            />
            {errors.cidade && <p className="text-sm text-red-500">{errors.cidade}</p>}
          </div>
          <div className="space-y-2">
            <Label>Estado <span className="text-red-500">*</span></Label>
            <Input 
              value={dados.estado} 
              onChange={e => {
                setDados({...dados, estado: e.target.value});
                clearError('estado');
              }}
              className={errors.estado ? 'border-red-500' : ''}
            />
            {errors.estado && <p className="text-sm text-red-500">{errors.estado}</p>}
          </div>
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Escopo dos Serviços (objeto do contrato)</h3>
        <p className="text-sm text-muted-foreground">
          Pré-preenchido a partir da proposta aceita. Ajuste se necessário — este texto alimenta a Cláusula do Objeto.
        </p>
        <div className="space-y-2">
          <Label>
            Itens / descrição <span className="text-red-500">*</span>
          </Label>
          <Textarea
            rows={6}
            value={dados.escopo_resumo}
            onChange={(e) => {
              setDados({ ...dados, escopo_resumo: e.target.value });
              clearError('escopo_resumo');
            }}
            className={errors.escopo_resumo ? 'border-red-500' : ''}
            placeholder="Liste os serviços contratados (um por linha)"
          />
          {errors.escopo_resumo && <p className="text-sm text-red-500">{errors.escopo_resumo}</p>}
        </div>
      </div>

      <Separator />

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Condições de Pagamento</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Modalidade de Pagamento <span className="text-red-500">*</span></Label>
            <Select 
              value={dados.modalidade_pagamento} 
              onValueChange={aplicarModalidadePagamento}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione a modalidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="integral_assinatura">Pagamento Integral na assinatura</SelectItem>
                <SelectItem value="assinatura_conclusao">Assinatura + Conclusão (2 parcelas)</SelectItem>
                <SelectItem value="tres_parcelas">Pagamento em 3 parcelas</SelectItem>
                <SelectItem value="integral_conclusao">Pagamento Integral na conclusão</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Valor Total (R$) <span className="text-red-500">*</span></Label>
            <Input 
              type="number" 
              step="0.01" 
              value={dados.valor_total} 
              onChange={e => {
                const total = Number(e.target.value);
                setDados({
                  ...dados, 
                  valor_total: total,
                  valor_parcela: Number((total / dados.numero_parcelas).toFixed(2))
                });
                clearError('valor_total');
              }}
              className={errors.valor_total ? 'border-red-500' : ''}
            />
            {errors.valor_total && <p className="text-sm text-red-500">{errors.valor_total}</p>}
          </div>
          {!modalidadeComEtapas && (
            <div className="space-y-2">
              <Label>Forma de Pagamento <span className="text-red-500">*</span></Label>
              <Select 
                value={dados.forma_pagamento} 
                onValueChange={value => {
                  setDados({...dados, forma_pagamento: value});
                  clearError('forma_pagamento');
                }}
              >
                <SelectTrigger className={errors.forma_pagamento ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Selecione a forma de pagamento" />
                </SelectTrigger>
                <SelectContent>
                  {opcoesFormaPagamento}
                </SelectContent>
              </Select>
              {errors.forma_pagamento && <p className="text-sm text-red-500">{errors.forma_pagamento}</p>}
            </div>
          )}

          {!modalidadeComEtapas && dados.forma_pagamento === 'cartão de crédito' && (
            <div className="md:col-span-2 p-4 bg-muted/30 rounded-lg border space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Número de Parcelas</Label>
                  <Select 
                    value={dados.numero_parcelas.toString()} 
                    onValueChange={value => {
                      const num = parseInt(value);
                      setDados({
                        ...dados, 
                        numero_parcelas: num,
                        valor_parcela: Number((dados.valor_total / num).toFixed(2))
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o nº de parcelas" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(12)].map((_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}x</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Valor da Parcela (R$)</Label>
                  <Input 
                    type="number" 
                    step="0.01"
                    value={dados.valor_parcela} 
                    onChange={e => setDados({...dados, valor_parcela: Number(e.target.value)})}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Total: {(dados.numero_parcelas * dados.valor_parcela).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {dados.modalidade_pagamento === 'assinatura_conclusao' && (
            <div className="md:col-span-2 p-6 bg-muted/30 rounded-lg border space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Lado da Assinatura */}
                <div className="space-y-4 p-4 bg-white rounded-md border shadow-sm">
                  <h4 className="font-bold text-sm border-b pb-2">Etapa 1: Assinatura</h4>
                  <div className="space-y-2">
                    <Label>Percentual (%)</Label>
                    <Input 
                      type="number" 
                      min="0" max="100"
                      value={dados.percentual_assinatura} 
                      onChange={e => {
                        const perc = Number(e.target.value);
                        const valorEtapa = (dados.valor_total * perc) / 100;
                        setDados({
                          ...dados, 
                          percentual_assinatura: perc,
                          valor_parcela_assinatura: Number((valorEtapa / dados.numero_parcelas_assinatura).toFixed(2))
                        });
                        clearError('percentuais');
                      }}
                    />
                    <p className="text-xs text-muted-foreground font-medium">
                      Valor desta etapa: {((dados.valor_total * dados.percentual_assinatura) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Forma de Pagamento</Label>
                    <Select 
                      value={dados.forma_pagamento_assinatura} 
                      onValueChange={value => setDados({...dados, forma_pagamento_assinatura: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {opcoesFormaPagamento}
                      </SelectContent>
                    </Select>
                  </div>
                  {dados.forma_pagamento_assinatura === 'cartão de crédito' && (
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t mt-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Parcelas</Label>
                        <Select 
                          value={dados.numero_parcelas_assinatura.toString()} 
                          onValueChange={value => {
                            const num = parseInt(value);
                            const valorEtapa = (dados.valor_total * dados.percentual_assinatura) / 100;
                            setDados({...dados, numero_parcelas_assinatura: num, valor_parcela_assinatura: Number((valorEtapa / num).toFixed(2))});
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[...Array(12)].map((_, i) => (
                              <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}x</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Valor Parcela (R$)</Label>
                        <Input 
                          type="number" step="0.01" 
                          className="h-8 text-xs"
                          value={dados.valor_parcela_assinatura} 
                          onChange={e => setDados({...dados, valor_parcela_assinatura: Number(e.target.value)})}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Lado da Conclusão */}
                <div className="space-y-4 p-4 bg-white rounded-md border shadow-sm">
                  <h4 className="font-bold text-sm border-b pb-2">Etapa 2: Conclusão</h4>
                  <div className="space-y-2">
                    <Label>Percentual (%)</Label>
                    <Input 
                      type="number" 
                      min="0" max="100"
                      value={dados.percentual_conclusao} 
                      onChange={e => {
                        const perc = Number(e.target.value);
                        const valorEtapa = (dados.valor_total * perc) / 100;
                        setDados({
                          ...dados, 
                          percentual_conclusao: perc,
                          valor_parcela_conclusao: Number((valorEtapa / dados.numero_parcelas_conclusao).toFixed(2))
                        });
                        clearError('percentuais');
                      }}
                    />
                    <p className="text-xs text-muted-foreground font-medium">
                      Valor desta etapa: {((dados.valor_total * dados.percentual_conclusao) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Forma de Pagamento</Label>
                    <Select 
                      value={dados.forma_pagamento_conclusao} 
                      onValueChange={value => setDados({...dados, forma_pagamento_conclusao: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {opcoesFormaPagamento}
                      </SelectContent>
                    </Select>
                  </div>
                  {dados.forma_pagamento_conclusao === 'cartão de crédito' && (
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t mt-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Parcelas</Label>
                        <Select 
                          value={dados.numero_parcelas_conclusao.toString()} 
                          onValueChange={value => {
                            const num = parseInt(value);
                            const valorEtapa = (dados.valor_total * dados.percentual_conclusao) / 100;
                            setDados({...dados, numero_parcelas_conclusao: num, valor_parcela_conclusao: Number((valorEtapa / num).toFixed(2))});
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[...Array(12)].map((_, i) => (
                              <SelectItem key={i + 1} value={(i + 1).toString()}>{i + 1}x</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Valor Parcela (R$)</Label>
                        <Input 
                          type="number" step="0.01" 
                          className="h-8 text-xs"
                          value={dados.valor_parcela_conclusao} 
                          onChange={e => setDados({...dados, valor_parcela_conclusao: Number(e.target.value)})}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-3 bg-white rounded border flex justify-between items-center">
                <div className="text-sm font-semibold">Soma dos Percentuais:</div>
                <div className={Number(dados.percentual_assinatura) + Number(dados.percentual_conclusao) === 100 ? 'text-green-600 font-bold text-lg' : 'text-red-600 font-bold text-lg'}>
                  {Number(dados.percentual_assinatura) + Number(dados.percentual_conclusao)}%
                </div>
              </div>
              {errors.percentuais && <p className="text-sm text-red-500 font-medium text-center">{errors.percentuais}</p>}
            </div>
          )}

          {dados.modalidade_pagamento === 'tres_parcelas' && (
            <div className="md:col-span-2 p-6 bg-muted/30 rounded-lg border space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-4 p-4 bg-white rounded-md border shadow-sm">
                  <h4 className="font-bold text-sm border-b pb-2">Etapa 1: Assinatura</h4>
                  <div className="space-y-2">
                    <Label>Percentual (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={dados.percentual_assinatura}
                      onChange={e => {
                        const perc = Number(e.target.value);
                        const valorEtapa = (dados.valor_total * perc) / 100;
                        setDados({
                          ...dados,
                          percentual_assinatura: perc,
                          valor_parcela_assinatura: Number((valorEtapa / dados.numero_parcelas_assinatura).toFixed(2)),
                        });
                        clearError('percentuais');
                      }}
                    />
                    <p className="text-xs text-muted-foreground font-medium">
                      Valor desta etapa:{' '}
                      {((dados.valor_total * dados.percentual_assinatura) / 100).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Forma de Pagamento</Label>
                    <Select
                      value={dados.forma_pagamento_assinatura}
                      onValueChange={value => setDados({ ...dados, forma_pagamento_assinatura: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>{opcoesFormaPagamento}</SelectContent>
                    </Select>
                  </div>
                  {dados.forma_pagamento_assinatura === 'cartão de crédito' && (
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t mt-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Parcelas</Label>
                        <Select
                          value={dados.numero_parcelas_assinatura.toString()}
                          onValueChange={value => {
                            const num = parseInt(value);
                            const valorEtapa = (dados.valor_total * dados.percentual_assinatura) / 100;
                            setDados({
                              ...dados,
                              numero_parcelas_assinatura: num,
                              valor_parcela_assinatura: Number((valorEtapa / num).toFixed(2)),
                            });
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[...Array(12)].map((_, i) => (
                              <SelectItem key={i + 1} value={(i + 1).toString()}>
                                {i + 1}x
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Valor Parcela (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          className="h-8 text-xs"
                          value={dados.valor_parcela_assinatura}
                          onChange={e => setDados({ ...dados, valor_parcela_assinatura: Number(e.target.value) })}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4 p-4 bg-white rounded-md border shadow-sm">
                  <h4 className="font-bold text-sm border-b pb-2">
                    {isLinhaFv ? 'Etapa 2: Início dos serviços' : 'Etapa 2: Início do projeto'}
                  </h4>
                  <div className="space-y-2">
                    <Label>Percentual (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={dados.percentual_intermediaria}
                      onChange={e => {
                        const perc = Number(e.target.value);
                        const valorEtapa = (dados.valor_total * perc) / 100;
                        setDados({
                          ...dados,
                          percentual_intermediaria: perc,
                          valor_parcela_intermediaria: Number(
                            (valorEtapa / dados.numero_parcelas_intermediaria).toFixed(2)
                          ),
                        });
                        clearError('percentuais');
                      }}
                    />
                    <p className="text-xs text-muted-foreground font-medium">
                      Valor desta etapa:{' '}
                      {((dados.valor_total * dados.percentual_intermediaria) / 100).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Forma de Pagamento</Label>
                    <Select
                      value={dados.forma_pagamento_intermediaria}
                      onValueChange={value => setDados({ ...dados, forma_pagamento_intermediaria: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>{opcoesFormaPagamento}</SelectContent>
                    </Select>
                  </div>
                  {dados.forma_pagamento_intermediaria === 'cartão de crédito' && (
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t mt-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Parcelas</Label>
                        <Select
                          value={dados.numero_parcelas_intermediaria.toString()}
                          onValueChange={value => {
                            const num = parseInt(value);
                            const valorEtapa = (dados.valor_total * dados.percentual_intermediaria) / 100;
                            setDados({
                              ...dados,
                              numero_parcelas_intermediaria: num,
                              valor_parcela_intermediaria: Number((valorEtapa / num).toFixed(2)),
                            });
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[...Array(12)].map((_, i) => (
                              <SelectItem key={i + 1} value={(i + 1).toString()}>
                                {i + 1}x
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Valor Parcela (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          className="h-8 text-xs"
                          value={dados.valor_parcela_intermediaria}
                          onChange={e => setDados({ ...dados, valor_parcela_intermediaria: Number(e.target.value) })}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4 p-4 bg-white rounded-md border shadow-sm">
                  <h4 className="font-bold text-sm border-b pb-2">
                    {isLinhaFv ? 'Etapa 3: Conclusão' : 'Etapa 3: Entrega do projeto'}
                  </h4>
                  <div className="space-y-2">
                    <Label>Percentual (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={dados.percentual_conclusao}
                      onChange={e => {
                        const perc = Number(e.target.value);
                        const valorEtapa = (dados.valor_total * perc) / 100;
                        setDados({
                          ...dados,
                          percentual_conclusao: perc,
                          valor_parcela_conclusao: Number((valorEtapa / dados.numero_parcelas_conclusao).toFixed(2)),
                        });
                        clearError('percentuais');
                      }}
                    />
                    <p className="text-xs text-muted-foreground font-medium">
                      Valor desta etapa:{' '}
                      {((dados.valor_total * dados.percentual_conclusao) / 100).toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Forma de Pagamento</Label>
                    <Select
                      value={dados.forma_pagamento_conclusao}
                      onValueChange={value => setDados({ ...dados, forma_pagamento_conclusao: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>{opcoesFormaPagamento}</SelectContent>
                    </Select>
                  </div>
                  {dados.forma_pagamento_conclusao === 'cartão de crédito' && (
                    <div className="grid grid-cols-2 gap-2 pt-2 border-t mt-2">
                      <div className="space-y-1">
                        <Label className="text-[10px]">Parcelas</Label>
                        <Select
                          value={dados.numero_parcelas_conclusao.toString()}
                          onValueChange={value => {
                            const num = parseInt(value);
                            const valorEtapa = (dados.valor_total * dados.percentual_conclusao) / 100;
                            setDados({
                              ...dados,
                              numero_parcelas_conclusao: num,
                              valor_parcela_conclusao: Number((valorEtapa / num).toFixed(2)),
                            });
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[...Array(12)].map((_, i) => (
                              <SelectItem key={i + 1} value={(i + 1).toString()}>
                                {i + 1}x
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px]">Valor Parcela (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          className="h-8 text-xs"
                          value={dados.valor_parcela_conclusao}
                          onChange={e => setDados({ ...dados, valor_parcela_conclusao: Number(e.target.value) })}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-3 bg-white rounded border flex justify-between items-center">
                <div className="text-sm font-semibold">Soma dos Percentuais:</div>
                <div
                  className={
                    Number(dados.percentual_assinatura) +
                      Number(dados.percentual_intermediaria) +
                      Number(dados.percentual_conclusao) ===
                    100
                      ? 'text-green-600 font-bold text-lg'
                      : 'text-red-600 font-bold text-lg'
                  }
                >
                  {Number(dados.percentual_assinatura) +
                    Number(dados.percentual_intermediaria) +
                    Number(dados.percentual_conclusao)}
                  %
                </div>
              </div>
              {errors.percentuais && (
                <p className="text-sm text-red-500 font-medium text-center">{errors.percentuais}</p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>Cidade de Assinatura <span className="text-red-500">*</span></Label>
            <Input 
              value={dados.cidade_assinatura} 
              onChange={e => {
                setDados({...dados, cidade_assinatura: e.target.value});
                clearError('cidade_assinatura');
              }}
              className={errors.cidade_assinatura ? 'border-red-500' : ''}
            />
            {errors.cidade_assinatura && <p className="text-sm text-red-500">{errors.cidade_assinatura}</p>}
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <Button variant="outline" className="flex-1" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button className="flex-1" onClick={handleGerarContrato} disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Gerando...
            </>
          ) : (
            <>
              <FileText className="mr-2 h-4 w-4" />
              Gerar Contrato Final
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
